import { useEffect, useState } from "react";
import { Cloud, CloudUpload, RefreshCw, Trash2, FilePlus2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { listSchools, saveSchool, getSchool, deleteSchool } from "@/lib/cloudStorage";

export default function SchoolSelector({
  state,
  cloudId,
  onLoadCloud,
  onSavedToCloud,
  onResetDraft,
  onClearCloudId,
}) {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    try {
      setLoading(true);
      const data = await listSchools();
      setSchools(data);
    } catch (err) {
      toast.error("Could not load saved schools");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleSelect = async (id) => {
    if (!id) return;
    try {
      const record = await getSchool(id);
      onLoadCloud({
        id: record.id,
        schoolInfo: record.schoolInfo,
        vendorInfo: record.vendorInfo,
        months: record.months,
      });
      toast.success(`Loaded ${record.schoolInfo?.schoolName || "school"}`);
    } catch (err) {
      toast.error("Failed to load school");
      console.error(err);
    }
  };

  const handleSave = async () => {
    if (!state.schoolInfo.schoolName?.trim() || !state.schoolInfo.udiseCode?.trim()) {
      toast.error("Add School Name and UDISE Code before saving to cloud");
      return;
    }
    try {
      setSaving(true);
      const res = await saveSchool({ id: cloudId, state });
      onSavedToCloud(res.id);
      await refresh();
      toast.success("Saved to cloud");
    } catch (err) {
      toast.error("Could not save to cloud");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!cloudId) return;
    try {
      await deleteSchool(cloudId);
      await refresh();
      onClearCloudId();
      toast.success("Cloud record deleted");
    } catch (err) {
      toast.error("Could not delete cloud record");
      console.error(err);
    }
  };

  return (
    <div
      data-testid="school-selector"
      className="border-b border-border bg-white no-print"
    >
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-2 text-ink-700">
          <Cloud className="h-4 w-4 text-clay" />
          <span className="text-xs font-semibold uppercase tracking-[0.18em]">Saved Schools</span>
        </div>

        <div className="min-w-[220px] flex-1 max-w-md">
          <Select onValueChange={handleSelect} value={cloudId || ""}>
            <SelectTrigger
              data-testid="school-selector-trigger"
              className="h-10 bg-white border-border"
            >
              <SelectValue placeholder={loading ? "Loading…" : "Select a saved school to load"} />
            </SelectTrigger>
            <SelectContent>
              {schools.length === 0 && (
                <div className="px-3 py-2 text-sm text-ink-700">No schools saved yet</div>
              )}
              {schools.map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`school-option-${s.udiseCode || s.id}`}>
                  <span className="font-medium">{s.schoolName || "Untitled"}</span>
                  <span className="ml-2 text-xs text-ink-700">
                    {s.udiseCode} · {s.financialYear} · {s.quarter}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          data-testid="school-selector-refresh"
          className="gap-2"
        >
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-cloud-btn"
            className="gap-2 bg-clay text-white hover:bg-clay-dark"
          >
            <CloudUpload className="h-4 w-4" /> {saving ? "Saving…" : cloudId ? "Update Cloud" : "Save to Cloud"}
          </Button>

          {cloudId && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="delete-cloud-btn"
                  className="gap-2 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete Cloud Copy
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete saved school from cloud?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This removes the cloud record only. Local browser data is kept.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                data-testid="new-draft-btn"
                className="gap-2 text-ink-700"
              >
                <FilePlus2 className="h-3.5 w-3.5" /> New Draft
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Start a new draft?</AlertDialogTitle>
                <AlertDialogDescription>
                  This clears the current data in this browser. Cloud copies are not affected.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onResetDraft}>Start Fresh</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}
